import { BriefcaseBusinessIcon } from 'lucide-react'
import { AvatarIcon } from '~/@mid_ui/icons/AvatarIcon'
import { useSettings } from '~/@mid_settings/settings'
import { TopNavigation } from '~/@mid_ui/top-navigation/TopNavigation'
import { TopNavLink } from '~/@mid_ui/top-navigation/TopNavLink'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import { Text } from '~/@mid_ui/typography/Text'
import { useOnboarding } from '~/@pages_onboarding/useOnboarding'
import { useFeeImmunity } from '~/@pages_investing/fee-immunity/useFeeImmunity'
import { useUser } from '../useUser/useUser'
import type { UserWithMeta } from '../useUser/types'
import { AccountMenu } from './account-menu/AccountMenu'
import { MobileNavigationMenu } from './mobile-navigation-menu/MobileNavigationMenu'

type Props = {
  colorScheme?: 'light' | 'dark'
  showAllItems?: boolean
}

function AccountTopNavigation({
  colorScheme = 'dark',
  showAllItems = true,
}: Props) {
  const isDesktop = useIsDesktop()
  const { getLandingPageLink } = useSettings()

  const { user } = useUser()
  const { hasVerifiedPhone } = useOnboarding(user ?? null)

  const isDark = colorScheme === 'dark'

  const accountName = (() => {
    if (!user) return 'Menu'

    return user?.profile.isCompany !== true && user?.profile.company?.name ? (
      <span className="flex items-center gap-2 text-sm font-semibold uppercase">
        <BriefcaseBusinessIcon />
        {user.profile.company.name}
      </span>
    ) : (
      <span className="flex items-center gap-2 text-sm font-semibold uppercase">
        <AvatarIcon
          className={`w-6! h-6! ${isDark || !isDesktop ? 'text-primary-foreground' : 'text-primary'}`}
        />

        {getUsername(user)}
      </span>
    )
  })()

  const { showFeeImmunityLink } = useFeeImmunity()

  return (
    <TopNavigation colorScheme={colorScheme} homeLinkPath="/">
      <div className="flex h-full text-sm font-semibold uppercase">
        <div className="flex h-full gap-0">
          {isDesktop && (
            <>
              {showAllItems && hasVerifiedPhone && (
                <>
                  <TopNavLink
                    to="/projects"
                    isActivePathCallback={(path) => path === '/'}
                    colorScheme={colorScheme}>
                    Otvorené príležitosti
                  </TopNavLink>
                  <TopNavLink
                    to="/account"
                    isActivePathCallback={(path) => path.startsWith('/account')}
                    colorScheme={colorScheme}>
                    Moje portfólio
                  </TopNavLink>
                </>
              )}

              <TopNavLink
                to={getLandingPageLink('faq')}
                target="_blank"
                colorScheme={colorScheme}>
                FAQ
              </TopNavLink>
            </>
          )}
        </div>
        {isDesktop ? (
          <AccountMenu
            triggerClassname={showAllItems ? 'px-5 ml-10' : ''}
            isProfileLinkVisible={hasVerifiedPhone}
            showFeeImmunityLink={showFeeImmunityLink}
            accountName={<Text>{accountName}</Text>}
          />
        ) : (
          <MobileNavigationMenu
            colorScheme={colorScheme}
            isProfileLinkVisible={hasVerifiedPhone}
            showFeeImmunityLink={showFeeImmunityLink}
            accountName={<Text>{accountName}</Text>}
            showAllItems={showAllItems}
          />
        )}
      </div>
    </TopNavigation>
  )
}

function getUsername(user: UserWithMeta) {
  const { lastname, firstname, email } = user.profile

  if (firstname && lastname) {
    const lastName = lastname.charAt(0).toLocaleUpperCase() + lastname.slice(1)
    const firstName = firstname.charAt(0).toLocaleUpperCase()

    return `${lastName}\xa0${firstName}.`
  }

  return email?.substring(0, 20) ?? ''
}

export { AccountTopNavigation }
